import { useCallback, useEffect, useRef } from 'react';

import { usePetitionActionsPageData } from '#src/app/pages/actions/pageContext';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { useVideosComponentContext } from '../../../../context';
import { useCamera } from '../../../../shared/hooks/useCamera';
import { useFinishTake } from '../../../../shared/hooks/useFinishTake';
import { useRecordingDuration } from '../../../../shared/hooks/useRecordingDuration';
import { useVideoRecorderActions } from '../../../../shared/hooks/useVideoRecorderActions';

type Result = ModelHookResult<
	{
		stream: MediaStream | null;
		isRecording: boolean;
		duration: number | undefined;
		shouldDisplayUploadButton: boolean;
		errorMessage: string | null;
	},
	{
		handleClick: () => void;
	}
>;

// oxlint-disable-next-line max-statements -- exceeds the 18-statement limit; flagged as documented debt rather than refactored, per team decision
export function useVideoRecorder(): Result {
	const {
		data: { errorMessage },
	} = useVideosComponentContext();

	const { track } = useCampaignTracking();
	const { openCamera } = useCamera();
	const {
		data: { isRecording, stream },
		actions: { startRecording, stopRecording, setUploadStep, setVideoSource },
	} = useVideoRecorderActions();

	const {
		data: {
			featureConfigs: { campaignActionsConfig },
		},
	} = usePetitionActionsPageData();

	// The clock has to be set up before the handler that stops it, because that handler needs the elapsed
	// time the clock is holding. The ref breaks the loop; the clock re-reads it on every fire.
	const finishRecordingRef = useRef<() => void>(() => {});

	const { duration, getElapsedSeconds } = useRecordingDuration({
		isRecording,
		onMaxDuration: () => {
			track('create_video_max_duration_reached');
			finishRecordingRef.current();
		},
		onBackgrounded: () => {
			track('create_video_stopped_backgrounded');
			finishRecordingRef.current();
		},
	});

	const finishTake = useFinishTake({ stopRecording, getElapsedSeconds, setVideoSource });

	const finishRecording = useCallback(() => {
		finishTake();
		setUploadStep('submit');
	}, [finishTake, setUploadStep]);

	useEffect(() => {
		finishRecordingRef.current = finishRecording;
	}, [finishRecording]);

	const handleClick = useCallback(() => {
		if (!isRecording) {
			track('campaign_action_create_video_start_recording_click');
			void startRecording();
			return;
		}

		track('campaign_action_create_video_stop_recording_click');
		finishRecording();
	}, [isRecording, startRecording, finishRecording, track]);

	useEffect(() => {
		void openCamera();
	}, [openCamera]);

	return {
		data: {
			stream,
			isRecording,
			duration,
			shouldDisplayUploadButton: !!campaignActionsConfig?.isUploadEnabled,
			errorMessage,
		},
		actions: {
			handleClick,
		},
	};
}
